import {
  Button,
  Loader,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  PanelRow,
} from '@teamsnap/teamsnap-ui';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import * as React from 'react';

// Local Imports
import Modal from 'components/common/modal';
import { AUTOCOMPLETE } from 'components/form/create-account-drawer';
import FormElementHook, {
  useFormDefaults,
} from 'components/form/form-element-hook';
import { convertFromPennies } from 'core/utils';
import {
  LoadingType,
  useOrderDetails,
  useOrderDetailsActions,
} from 'hooks/orderDetails';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import ENVIRONMENT from '../../../core/environment';
import '../modal-styles.scss';
import { TransactionRow } from './types';

enum RefundType {
  ONLINE = 'online',
  OFFLINE = 'offline',
}

type Props = {
  isOpen: boolean;
  closeFn: () => void;
  transaction: TransactionRow;
};

const CreateElement = FormElementHook as new () => FormElementHook;

export const RefundTransactionModal: React.FC<Props> = ({
  isOpen,
  closeFn,
  transaction,
}) => {
  const [refundType, setRefundType] = useState<RefundType>(RefundType.ONLINE);
  const [paymentOptions, setPaymentOptions] = useState<
    { text: string; value: string }[]
  >([]);
  const refundForm = useForm();
  useFormDefaults(transaction, setFormDefaults, [transaction.id, refundType]);

  const { order, loading } = useOrderDetails();
  const { refundTransaction, getTransactions } = useOrderDetailsActions();
  const { handleSubmit, register } = refundForm;

  useEffect(() => {
    const options = [{ text: 'Offline', value: RefundType.OFFLINE }];
    const { paymentMethodType } = transaction;
    if (
      paymentMethodType &&
      !paymentMethodType.toLowerCase().includes(RefundType.OFFLINE)
    ) {
      options.unshift({ text: 'Online', value: RefundType.ONLINE });
    } else {
      setRefundType(RefundType.OFFLINE);
    }
    setPaymentOptions(options);
  }, [transaction.paymentMethodType]);

  const isOffline = refundType === RefundType.OFFLINE;
  const autoComplete: string = AUTOCOMPLETE.OFF;
  const isLoading = loading[LoadingType.RefundTransaction];

  const amountMinusFeesToString = (
    (transaction.amount - transaction.appliedFeeAmount || 0) / 100
  ).toFixed(2);

  function setFormDefaults() {
    const amountToDollar = (transaction.amount / 100).toFixed(2);
    const formValues = {};

    formValues['amount'] = amountToDollar;
    formValues['type'] = refundType;

    Object.entries(formValues).forEach(([k, v]) => refundForm.setValue(k, v));
  }

  const handleRefundType = (e) => {
    setRefundType(e.target.value);
  };

  const validateRefundAmount = (value) => {
    const refundAmountInCents = parseFloat((value * 100).toFixed(2));
    const amountRemainingInCents = transaction.amount - refundAmountInCents;

    // Are we doing a full refund? If not, check to make sure refund is valid.
    const isAllowed =
      amountRemainingInCents === 0 ||
      amountRemainingInCents >= (transaction.appliedFeeAmount ?? 0);

    if (amountRemainingInCents < 0) {
      return false || 'You cannot refund more than the remaining balance.';
    }

    return isAllowed;
  };

  const resetAndCloseForm = () => {
    refundForm.reset();
    closeFn();
  };

  const submitRefund = async (values) => {
    try {
      const amount = parseFloat((values.amount * 100).toFixed(2));

      const refund = await refundTransaction(
        order?.id,
        Number(transaction.id),
        +amount,
        values.note,
        values.type
      );

      if (refund?.id) {
        reactiveFeedbackMessage({
          message: `Success! Refund issued for Transaction ${transaction.id}.`,
          autoclose: true,
          isError: false,
        });
        getTransactions(order?.id);
        resetAndCloseForm();
      } else {
        reactiveFeedbackMessage({
          message: `Unable to issue a refund for Transaction ${transaction.id}.`,
          autoclose: true,
          isError: true,
        });
      }
    } catch (error) {
      //@ts-ignore
      if (ENVIRONMENT !== 'prod' && ENVIRONMENT !== 'production') {
        console.error(error.message);
      }

      reactiveFeedbackMessage({
        message: `Oops, something went wrong! Unable to issue a refund for Transaction ${transaction.id}. Error: ${error.message}`,
        autoclose: false,
        isError: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={closeFn}>
      <Panel>
        <PanelHeader
          className="Panel-header"
          title={`Issue refund for Transaction${
            transaction.id ? ` ${transaction.id}` : '.'
          }`}
          testId="refund-transaction-modal--container-header"
        />
        <PanelBody
          className="Panel-body"
          testId="refund-transaction-modal--container-body"
        >
          <PanelRow testId="refund-transaction-modal--container-row">
            <div
              className="u-padXs u-spaceEndsXs"
              data-testid="refund-transaction-modal--how-to-issue-refund-label"
            >
              <CreateElement
                disabled={loading[LoadingType.RefundTransaction]}
                onChange={handleRefundType}
                useHookResponse={refundForm}
                inputRef={register({
                  required:
                    'You must indicate how you would like to issue this refund.',
                })}
                type="select"
                field="type"
                defaultValue={refundType}
                label={
                  <>
                    How do you want to issue the refund?
                    <span className="u-colorNegative">*</span>
                  </>
                }
                options={paymentOptions}
              />
            </div>

            <div
              className="u-padXs u-spaceEndsXs refund-transaction-modal"
              data-testid="refund-transaction-modal--amount-label"
            >
              <CreateElement
                disabled={isLoading}
                inputRef={register({
                  required: 'You must enter a refund amount.',
                  validate: (value) =>
                    validateRefundAmount(value) ||
                    'Cannot refund online fees as part of a partial refund.',
                })}
                useHookResponse={refundForm}
                numberInputAttributes={{ max: 999999999999, min: 0 }}
                type="number"
                leftIcon={<span style={{ color: '#98A4AE' }}>$</span>}
                field="amount"
                label={
                  <>
                    Amount
                    <span className="u-colorNegative">*</span>
                  </>
                }
                placeholder={`${convertFromPennies(transaction.amount)}`}
              />

              <small className="u-colorNeutral7">
                {transaction.appliedFeeAmount !== transaction.amount &&
                  `You can refund the full amount or any amount up to $${amountMinusFeesToString}.`}{' '}
                You cannot refund a partial amount in which the remaining
                transaction is less than the online fee.
              </small>
            </div>

            <div
              className="u-padXs u-spaceEndsXs"
              data-testid="refund-transaction-modal--refund-note-label"
            >
              <CreateElement
                disabled={isLoading}
                inputRef={register()}
                useHookResponse={refundForm}
                type="text"
                field="note"
                label="Refund note"
                placeholder=""
                autoComplete={autoComplete}
              />
            </div>

            <p
              className="u-colorNeutral7"
              data-testid="refund-transaction-modal--refund-issue-msg"
            >
              {isOffline
                ? 'Please note that the refund amount must be provided by you directly to the recipient in the form of cash or check. This action will only record the refund transaction for your financial records.'
                : 'Refund will be issued via original payment method.'}
            </p>
          </PanelRow>
        </PanelBody>
        <PanelFooter
          mods="u-text-left"
          testId="refund-transaction-modal--container-footer"
        >
          <div className="button-list">
            <Button
              isDisabled={isLoading}
              color="default"
              onClick={() => resetAndCloseForm()}
              testId="refund-transaction-modal--cancel-button"
            >
              Cancel
            </Button>

            <Button
              isDisabled={isLoading}
              mods="Button--negative"
              type="submit"
              onClick={() => {
                handleSubmit(submitRefund)();
              }}
              testId="refund-transaction-modal--refund-button"
            >
              {isLoading && (
                <Loader type="spin" style={{ width: '13px', height: '13px' }} />
              )}
              {!isLoading &&
                `Yes, ${isOffline ? 'record offline refund' : 'issue refund'}`}
            </Button>
          </div>
        </PanelFooter>
      </Panel>
    </Modal>
  );
};
